// Environment configuration with type safety and validation

interface EnvironmentConfig {
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
  };
  cache: {
    staticMaxAge: number;
    apiMaxAge: number;
    imageMaxAge: number;
    fontMaxAge: number;
    staticMaxEntries: number;
    apiMaxEntries: number;
    imageMaxEntries: number;
    fontMaxEntries: number;
  };
  features: {
    enableDevTools: boolean;
    enableErrorReporting: boolean;
    enableAnalytics: boolean;
  };
}

class Environment {
  private static instance: Environment;
  private config: EnvironmentConfig;

  private constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
  }

  static getInstance(): Environment {
    if (!Environment.instance) {
      Environment.instance = new Environment();
    }
    return Environment.instance;
  }

  private loadConfig(): EnvironmentConfig {
    return {
      api: {
        baseUrl: process.env.REACT_APP_PUBLIC_API_URL || 'http://localhost:3001/api/v1',
        timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
        retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS || '3', 10),
      },
      cache: {
        staticMaxAge: parseInt(process.env.REACT_APP_STATIC_CACHE_MAX_AGE || '2592000', 10),
        apiMaxAge: parseInt(process.env.REACT_APP_API_CACHE_MAX_AGE || '86400', 10),
        imageMaxAge: parseInt(process.env.REACT_APP_IMAGE_CACHE_MAX_AGE || '604800', 10),
        fontMaxAge: parseInt(process.env.REACT_APP_FONT_CACHE_MAX_AGE || '31536000', 10),
        staticMaxEntries: parseInt(process.env.REACT_APP_STATIC_CACHE_MAX_ENTRIES || '50', 10),
        apiMaxEntries: parseInt(process.env.REACT_APP_API_CACHE_MAX_ENTRIES || '100', 10),
        imageMaxEntries: parseInt(process.env.REACT_APP_IMAGE_CACHE_MAX_ENTRIES || '60', 10),
        fontMaxEntries: parseInt(process.env.REACT_APP_FONT_CACHE_MAX_ENTRIES || '10', 10),
      },
      features: {
        enableDevTools: process.env.NODE_ENV === 'development',
        enableErrorReporting: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true',
        enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
      },
    };
  }

  private validateConfig(): void {
    const { api, cache } = this.config;

    // Validate API config
    if (!api.baseUrl) {
      throw new Error('API base URL is required');
    }

    if (!api.baseUrl.startsWith('http://') && !api.baseUrl.startsWith('https://')) {
      throw new Error('API base URL must start with http:// or https://');
    }

    if (api.timeout <= 0) {
      throw new Error('API timeout must be positive');
    }

    if (api.retryAttempts < 0) {
      throw new Error('API retry attempts cannot be negative');
    }

    // Validate cache config
    Object.entries(cache).forEach(([key, value]) => {
      if (value < 0) {
        throw new Error(`Cache ${key} cannot be negative`);
      }
    });
  }

  get api() {
    return this.config.api;
  }

  get cache() {
    return this.config.cache;
  }

  get features() {
    return this.config.features;
  }

  get isDevelopment() {
    return process.env.NODE_ENV === 'development';
  }

  get isProduction() {
    return process.env.NODE_ENV === 'production';
  }

  get isTest() {
    return process.env.NODE_ENV === 'test';
  }
}

// Export singleton instance
export const env = Environment.getInstance();

// Export for easier access
export const API_BASE_URL = env.api.baseUrl;
export const API_TIMEOUT = env.api.timeout;
export const API_RETRY_ATTEMPTS = env.api.retryAttempts;

// Helper function to build API endpoints
export const buildApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${cleanEndpoint}`;
};